/**
 * Date utility functions for formatting timestamps
 */

/**
 * Format a date string or Date object to a localized string in IST
 * @param {string|Date} dateInput - Date string or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDateTime = (dateInput, options = {}) => {
  if (!dateInput) {
    return '-';
  }

  try {
    const date = new Date(dateInput);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '-';
    }

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata', // Convert to IST
      ...options
    };

    return date.toLocaleString('en-IN', defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

/**
 * Format a date for activity log display
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string for activity log
 */
export const formatActivityLogDate = (dateInput) => {
  const formatted = formatDateTime(dateInput, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  
  // Convert lowercase am/pm to uppercase AM/PM
  return formatted.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());
};

/**
 * Get current timestamp formatted for activity log
 * @returns {string} Current timestamp formatted for activity log
 */
export const getCurrentFormattedTime = () => {
  return formatActivityLogDate(new Date());
};

/**
 * Format date for short display (without time)
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateOnly = (dateInput) => {
  return formatDateTime(dateInput, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format time only (without date)
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted time string
 */
export const formatTimeOnly = (dateInput) => {
  return formatDateTime(dateInput, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};
